/**
 * The per-dossier audit trail the BRIEF asks for: uploads, pipeline runs, fact
 * edits, report exports, data-mode changes — who and when.
 *
 * It belongs to the dossier, not to the founder: the Büro that uploaded a
 * dossier is the party that has to be able to answer "who changed this?", so
 * the route is workspace-scoped and a member reaches their own. The founder
 * reaches it too, by the same scope rule that governs everything else.
 *
 * The retention window sits at the top rather than at the bottom: the first
 * question a trail raises is "how long will this still be here?".
 */
import { api } from '@/api/client'
import { ErrorNote } from '@/components/ErrorNote'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { AuditTable } from '@/features/audit/AuditTable'
import { RetentionBadge } from '@/features/retention/RetentionBadge'
import { DeleteDossierButton } from '@/features/retention/DeleteDossierButton'
import { useI18n } from '@/i18n'
import { Link, buildPath, navigate } from '@/lib/router'
import { useAsync } from '@/lib/useAsync'
import type { AuditEventRef, DossierRef, WorkspaceRef } from '@/api/types'

export function DossierAuditScreen({
  workspace,
  dossierId,
}: {
  workspace: WorkspaceRef
  dossierId: string
}) {
  const { t } = useI18n()
  const dossier = useAsync<DossierRef>(
    (signal) => api.dossier(workspace.id, dossierId, signal),
    [workspace.id, dossierId],
  )
  const events = useAsync<AuditEventRef[]>(
    (signal) => api.dossierAudit(workspace.id, dossierId, signal),
    [workspace.id, dossierId],
  )
  const back = buildPath('workspace', { workspaceId: workspace.id })

  return (
    <div className="flex flex-col gap-8">
      <section className="flex flex-col gap-3">
        <h1 className="font-display text-[32px] leading-tight font-semibold">
          {t('dossierAudit.title')}
        </h1>
        <p className="max-w-[70ch] text-[15px] text-muted">{t('dossierAudit.lead')}</p>
        {dossier.data ? (
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-[15px] text-ink">{dossier.data.title}</span>
            <RetentionBadge deleteAfter={dossier.data.delete_after} />
          </div>
        ) : null}
        <Link to={back} className="text-[13px] underline underline-offset-2">
          {t('dossierAudit.back')}
        </Link>
      </section>

      {dossier.error ? <ErrorNote message={dossier.error} onRetry={dossier.reload} retryLabel={t('common.retry')} /> : null}
      {events.error ? (
        <ErrorNote message={events.error} onRetry={events.reload} retryLabel={t('common.retry')} />
      ) : null}

      <section aria-labelledby="dossier-trail" className="flex flex-col gap-4">
        <h2 id="dossier-trail" className="font-display text-[21px] font-semibold">
          {t('audit.what')}
        </h2>
        <AuditTable events={events.data ?? []} caption={t('dossierAudit.title')} />
      </section>

      {dossier.data ? (
        <Card>
          <CardHeader>
            <CardTitle>{t('retention.title')}</CardTitle>
            <CardDescription>{t('retention.note')}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <p className="text-[13px] text-muted">
              {t('retention.window', { days: workspace.retention_days })}
            </p>
            <DeleteDossierButton
              dossier={dossier.data}
              // The dossier this screen is about no longer exists — staying on
              // it would show a 404 in three panels. Back to the workspace.
              onDeleted={() => navigate(back)}
            />
          </CardContent>
        </Card>
      ) : null}
    </div>
  )
}
